import React, { useEffect, useState } from "react";
import { DatabaseBackup, Loader2, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import client from "@/lib/api";

const fmtIST = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", dateStyle: "medium", timeStyle: "short" }) : "—");
const kb = (n) => (n ? `${(n / 1024).toFixed(1)} KB` : "—");

// Read-only daily backup status for admins, with a manual "Back up now".
export default function AdminBackupStatus() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => client.get("/admin/backups").then((r) => setS(r.data)).catch(() => setS({ alert: "Could not load backup status", runs: [] }));
  useEffect(() => { load(); }, []);
  const runNow = async () => {
    setBusy(true);
    try {
      await client.post("/admin/backups/run");
      toast.success("Backup started — status updates in a few seconds");
      setTimeout(() => { load(); setBusy(false); }, 8000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start backup"); setBusy(false); }
  };
  if (!s) return null;
  const ok = s.last_success;
  return (
    <div className={`rounded-2xl border p-4 mb-5 ${s.alert ? "border-rose-200 bg-rose-50" : "border-emerald-100 bg-emerald-50/60"}`} data-testid="admin-backup-status">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="font-display text-base font-bold text-slate-900 flex items-center gap-2"><DatabaseBackup className="w-4 h-4 text-emerald-700" /> Daily data backup</h2>
          <p className="text-xs text-slate-600 mt-0.5">{s.schedule} · Next: {fmtIST(s.next_run)} · {s.storage}</p>
        </div>
        <button onClick={runNow} disabled={busy} className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-white hover:bg-emerald-50 text-emerald-800 px-3 py-1.5 min-h-[36px] text-xs font-semibold disabled:opacity-60" data-testid="admin-backup-run">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <DatabaseBackup className="w-3.5 h-3.5" />} Back up now
        </button>
      </div>
      {s.alert ? (
        <div className="mt-3 flex items-start gap-2 text-sm text-rose-800" data-testid="admin-backup-alert"><AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" /> <span className="break-words min-w-0">{s.alert}</span></div>
      ) : (
        <div className="mt-3 flex items-start gap-2 text-sm text-emerald-900" data-testid="admin-backup-ok"><CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" /> <span>Last backup OK and verified</span></div>
      )}
      {ok && (
        <div className="mt-2 text-xs text-slate-600" data-testid="admin-backup-last">
          Last successful: {fmtIST(ok.finished_at)} · {ok.total_docs} records · {kb(ok.size_bytes)} · {ok.verified ? "integrity verified" : "not verified"}
          {ok.files_count != null && <span data-testid="admin-backup-files"> · {ok.files_count} uploaded files ({(ok.files_bytes / 1048576).toFixed(1)} MB){ok.files_verified ? " verified" : ""}</span>}
        </div>
      )}
      {s.retention && (
        <div className="mt-1 text-[11px] text-slate-500" data-testid="admin-backup-retention">
          Retention: {s.retention.policy} · {s.retention.successful} kept · {s.retention.eligible_for_removal.length} older than policy (report only, nothing deleted)
        </div>
      )}
      {s.runs?.length > 0 && (
        <ul className="mt-2 text-[11px] text-slate-500 space-y-0.5" data-testid="admin-backup-runs">
          {s.runs.slice(0, 5).map((r) => (
            <li key={r.backup_id} className="break-words">{fmtIST(r.started_at)} · <span className={r.status === "success" ? "text-emerald-700" : r.status === "failed" ? "text-rose-700" : "text-amber-700"}>{r.status}</span> · {r.trigger}{r.error ? ` · ${r.error}` : ""}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
